import { useMemo } from "react";
import {
  FP_CUSTOM_SOURCE_PREFIX,
  FP_TOTAL_WEIGHT_SOURCE_PREFIX,
  type PublicQuoteLine,
} from "@forgepoint/shared";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getTotalWeightsByUnit } from "@/modules/publicQuote/utils";
import { quoteFieldLabel } from "./quoteFields";
import {
  resolveSampleValue,
  sampleKeysForSource,
  SAMPLE_DATE_KEYS,
  SAMPLE_MARKDOWN_KEYS,
  type ConfiguredSampleValues,
  type SampleFieldKey,
} from "./sampleQuote";

/** Shown when the template binds nothing the sample form could fill in. */
const EMPTY_STATE = "This template does not bind any quote fields.";

/** Explains why the total-weight rows cannot be typed into. */
const TOTAL_WEIGHT_HINT = "computed from the sample lines";

/** One titled group of sample inputs, listed in the order it renders. */
interface SampleFieldGroup {
  /** The group heading. */
  title: string;
  /** The primitive keys the group owns, in display order. */
  keys: readonly SampleFieldKey[];
}

/** The groups the standard sample fields fall into, in display order. */
const SAMPLE_FIELD_GROUPS: readonly SampleFieldGroup[] = [
  {
    title: "Organization",
    keys: ["org-name", "org-address", "org-website", "org-phone", "org-fax"],
  },
  { title: "Location", keys: ["location-name", "location-address"] },
  {
    title: "Customer",
    keys: [
      "customer-company-name",
      "customer-company-phone",
      "customer-company-email",
      "customer-company-address",
      "quote-people-names",
      "quote-people-emails",
      "ship-to-address",
      "opportunity-name",
    ],
  },
  {
    title: "Quote",
    keys: [
      "quote-name",
      "quote-number",
      "sales-rep-name",
      "sales-rep-email",
      "estimator-names",
      "effective-date",
      "expiration-date",
      "subtotal",
      "subtotal-with-discounts",
      "total",
      "quote-footer",
    ],
  },
  {
    title: "Terms",
    keys: [
      "cover-letter",
      "payment-terms",
      "delivery-terms",
      "terms-of-service",
    ],
  },
  { title: "Signature", keys: ["signer-name", "signed-date"] },
];

interface SampleFieldsFormProps {
  /** The quote-scope sources the template binds, in document order. */
  quoteSources: readonly string[];
  /** The template's sample field overrides, keyed by sample key. */
  fields: Record<string, string>;
  /** The organization's configured values, standing in for invented defaults. */
  configured: ConfiguredSampleValues;
  /** The sample lines the read-only total-weight rows sum. */
  lines: readonly PublicQuoteLine[];
  /** Sets one sample field value. */
  onFieldChange: (key: string, value: string) => void;
}

/**
 * The quote-scope half of the sample form: one input per writable value the
 * template actually binds, grouped, with the derived total-weight values shown
 * read-only because they come from the sample lines rather than a typed value.
 * @param quoteSources - The quote-scope sources the template binds
 * @param fields - The template's sample field overrides
 * @param configured - The organization's configured values
 * @param lines - The sample lines the total-weight rows sum
 * @param onFieldChange - Sets one sample field value
 * @returns The grouped sample field inputs
 */
export function SampleFieldsForm({
  quoteSources,
  fields,
  configured,
  lines,
  onFieldChange,
}: SampleFieldsFormProps) {
  const usedKeys = useMemo(
    () => expandToSampleKeys(quoteSources),
    [quoteSources],
  );
  const customSources = useMemo(
    () =>
      quoteSources.filter((source) =>
        isPrefixed(source, FP_CUSTOM_SOURCE_PREFIX),
      ),
    [quoteSources],
  );
  const weightUnits = useMemo(
    () =>
      quoteSources
        .filter((source) => isPrefixed(source, FP_TOTAL_WEIGHT_SOURCE_PREFIX))
        .map((source) => source.slice(FP_TOTAL_WEIGHT_SOURCE_PREFIX.length)),
    [quoteSources],
  );
  const totals = useMemo(() => getTotalWeightsByUnit([...lines]), [lines]);

  const groups = SAMPLE_FIELD_GROUPS.map((group) => ({
    ...group,
    keys: group.keys.filter((key) => usedKeys.has(key)),
  })).filter((group) => group.keys.length > 0);

  if (
    groups.length === 0 &&
    customSources.length === 0 &&
    weightUnits.length === 0
  ) {
    return <p className="text-sm text-muted-foreground">{EMPTY_STATE}</p>;
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.title} className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group.title}
          </h3>
          {group.keys.map((key) => (
            <SampleFieldInput
              key={key}
              fieldKey={key}
              label={quoteFieldLabel(key) ?? key}
              value={resolveSampleValue(key, fields, configured)}
              onChange={onFieldChange}
            />
          ))}
        </section>
      ))}

      {customSources.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Custom fields
          </h3>
          {customSources.map((source) => (
            <div key={source} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor={`sample-${source}`}>
                  {source.slice(FP_CUSTOM_SOURCE_PREFIX.length)}
                </Label>
                <Badge variant="secondary">custom</Badge>
              </div>
              <Input
                id={`sample-${source}`}
                value={fields[source] ?? ""}
                onChange={(event) => onFieldChange(source, event.target.value)}
              />
            </div>
          ))}
        </section>
      )}

      {weightUnits.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Total weight
          </h3>
          {weightUnits.map((unit) => (
            <div key={unit} className="space-y-1">
              <p className="text-sm">
                {`Total weight (${unit}): `}
                {totals
                  .find(([weightUnit]) => weightUnit === unit)?.[1]
                  .toFixed(2) ?? "-"}
              </p>
              <p className="text-xs text-muted-foreground">
                {TOTAL_WEIGHT_HINT}
              </p>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

interface SampleFieldInputProps {
  /** The primitive sample key this input writes. */
  fieldKey: SampleFieldKey;
  /** The field's friendly label. */
  label: string;
  /** The current value. */
  value: string;
  /** Sets the value. */
  onChange: (key: string, value: string) => void;
}

/**
 * One sample field control, picking the input shape from the key: a date input
 * for calendar days, a textarea for markdown, plain text otherwise.
 * @param fieldKey - The primitive sample key this input writes
 * @param label - The field's friendly label
 * @param value - The current value
 * @param onChange - Sets the value
 * @returns The labelled input
 */
function SampleFieldInput({
  fieldKey,
  label,
  value,
  onChange,
}: SampleFieldInputProps) {
  const id = `sample-${fieldKey}`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {SAMPLE_MARKDOWN_KEYS.includes(fieldKey) ? (
        <Textarea
          id={id}
          minRows={3}
          value={value}
          onChange={(event) => onChange(fieldKey, event.target.value)}
        />
      ) : (
        <Input
          id={id}
          type={SAMPLE_DATE_KEYS.includes(fieldKey) ? "date" : "text"}
          value={value}
          onChange={(event) => onChange(fieldKey, event.target.value)}
        />
      )}
    </div>
  );
}

/**
 * The primitive sample keys a set of bound sources needs inputs for.
 * @param quoteSources - The quote-scope sources the template binds
 * @returns The distinct primitive keys to render
 */
function expandToSampleKeys(
  quoteSources: readonly string[],
): Set<SampleFieldKey> {
  return new Set(quoteSources.flatMap(sampleKeysForSource));
}

/**
 * Whether a source carries a parameterized prefix naming something.
 * @param source - The raw source key
 * @param prefix - The prefix to test for
 * @returns True when the key is prefixed and names something
 */
function isPrefixed(source: string, prefix: string): boolean {
  return source.startsWith(prefix) && source.slice(prefix.length).trim() !== "";
}
